import { Component, DestroyRef, OnDestroy, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellRange, CellType, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'
import { interval, switchMap, takeWhile } from 'rxjs'

import { BaseComponent, FeatureType, FuelEntry, Grid, ModalSize } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { FuelRecordComponent } from './fuel-record.component'
import { VistaExportModalComponent, VistaExportRange } from './vista-export-modal.component'

interface VistaImportStatus {
    id: string
    recordType: string
    status: string
    fileName: string
    rowCount: number
    errorText?: string
    logText?: string
    failureSummary?: string
    isTerminal: boolean
}

@Component({
    templateUrl: './fuel-records.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
    ],
})
export class FuelRecordsComponent extends BaseComponent implements OnDestroy {
    private activatedRoute = inject(ActivatedRoute)
    private destroyRef = inject(DestroyRef)

    faSearch = faSearch
    active = 1
    grid: Grid<FuelEntry>
    allEquipment: any[] = []
    allJobs: any[] = []
    linkedFuelRecordId = ''
    allRecords: FuelEntry[] = []

    vistaSending = false
    vistaStatus: VistaImportStatus | null = null

    constructor() {
        super()

        // Check to see if the user is coming here from a slack channel link or other link.
        this.linkedFuelRecordId = this.services.angular.getQueryParam('id', this.activatedRoute)

        this.grid = new Grid<FuelEntry>(this.services)
            .setupModalEditing(
                this.services.data.fuelEntries,
                'Fuel Record',
                FuelRecordComponent,
                () => {
                    const fuelEntry = new FuelEntry()
                    FuelEntry.addTableProps(fuelEntry)
                    return fuelEntry
                },
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.vehicle, item.source, item.userName])
            .wrapColumnHeaders()

        this.grid.itemEdited.on(this.updatePropsOnEdit.bind(this))
        this.queryPrereqs()
        this.refresh()
    }

    ngOnDestroy() {
        this.grid.itemEdited.off(this.updatePropsOnEdit)
    }

    queryPrereqs() {
        this.services.data
            .load(
                true,
                true,
                this.services.data.vpJobs.queryAll(),
                this.services.data.fuelRecordDefaults.queryAll(), // The defaults are applied in the fuel-entry.ts class
                this.services.data.get('/api/VpData/QueryFuelPrices'),
            )
            .subscribe({
                next: result => {
                    this.allJobs = result[0]
                    this.services.data.fuelPrices.items = result[2]
                },
            })
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.fuelEntries.queryAll()).subscribe({
                next: successResult => {
                    this.initialLoadingComplete()
                    const records = successResult[0]
                    for (const record of records) {
                        FuelEntry.addTableProps(record)
                    }
                    this.allRecords = successResult[0]
                    this.grid.items = this.allRecords
                    this.allJobs = successResult[1]

                    // If the user is coming here from a slack channel, open the record for editing.
                    if (this.linkedFuelRecordId) {
                        const itemToEdit = this.grid.items.find(x => x.id === this.linkedFuelRecordId)
                        if (itemToEdit) {
                            this.grid.selectedItem = itemToEdit
                            const row = this.grid.flexGrid.collectionView.items.indexOf(itemToEdit)
                            this.grid.flexGrid.select(new CellRange(row, 1), true)
                            this.grid.editItem()
                        } else this.services.notification.warning('The fuel record from the link could not be found.')
                    }
                },
            }),
        )
    }
    itemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            cell.classList.remove('yellow-cell')
            cell.classList.remove('green-cell')
            cell.classList.remove('red-cell')
            const item = panel.grid.rows[r].dataItem
            if (this.active == 1) {
                if (col.binding === 'source' || col.binding === 'fuelTypeOut') {
                    if (item.fuelTypeOut === 'D') cell.classList.add('green-cell')
                    if (item.fuelTypeOut === 'O') cell.classList.add('red-cell')
                }
                if (col.binding === 'vehicle' || col.binding === 'fuelTypeIn') {
                    if (item.fuelTypeIn === 'D') cell.classList.add('green-cell')
                    if (item.fuelTypeIn === 'O') cell.classList.add('red-cell')
                }
            }
        }
    }
    updatePropsOnEdit(args) {
        FuelEntry.addTableProps(args.newItem)
    }
    sendToVista() {
        this.services.modals.open(VistaExportModalComponent, ModalSize.medium).result.then(
            (range: VistaExportRange) => {
                if (!range) return
                this.vistaSending = true
                this.vistaStatus = null
                this.services.data
                    .post<VistaImportStatus>('api/VistaBatchImport/CreateFuelImport', range)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: job => {
                            this.vistaStatus = job
                            this.services.notification.info(
                                `Queued ${job.rowCount} fuel records for Vista. Monitoring progress...`,
                            )
                            this.pollVistaStatus(job.id)
                        },
                        error: err => {
                            this.vistaSending = false
                            this.services.notification.error(err.error?.message || 'Failed to queue the fuel import for Vista.')
                        },
                    })
            },
            () => {},
        )
    }

    private pollVistaStatus(id: string) {
        interval(5000)
            .pipe(
                switchMap(() => this.services.data.get<VistaImportStatus>(`api/VistaBatchImport/Status/${id}`)),
                takeWhile(job => !job.isTerminal, true),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: job => {
                    this.vistaStatus = job
                    if (!job.isTerminal) return
                    this.vistaSending = false
                    if (job.status === 'Succeeded') {
                        this.services.notification.success(`Vista import succeeded (${job.rowCount} records).`)
                    } else if (job.status === 'Failed') {
                        this.services.notification.error('Vista import failed. See details below.')
                    } else if (job.status === 'TimedOut') {
                        this.services.notification.warning('Vista import timed out. See details below.')
                    }
                },
                error: () => {
                    this.vistaSending = false
                    this.services.notification.error('Lost connection while monitoring the Vista import.')
                },
            })
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.EquipmentFuelRecordsEdit])
    }
    tabChanged() {
        const gridRecords = [] as FuelEntry[]
        for (let i = 0; i < this.allRecords.length; i++) {
            const record = this.allRecords[i]
            record.sequence = i + 1
            gridRecords.push(record)
        }
        this.services.data.fuelEntries.items = gridRecords
        this.grid.items = this.services.data.fuelEntries.items
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 100)
    }
}
